
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const LandingPage = () => {
  const navigate = useNavigate();
  const [isRevealed, setIsRevealed] = useState(false);
  
  const blueprintRef = useRef<HTMLDivElement>(null);

  const handleDeepLink = (webUrl: string, appUrl?: string) => {
    if (appUrl && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) {
      const start = Date.now();
      window.location.href = appUrl;
      
      // 앱이 설치되어 있지 않으면 1.5초 후 웹으로 이동
      setTimeout(() => {
        if (Date.now() - start < 2000) {
          window.open(webUrl, '_blank');
        }
      }, 1500);
    } else {
      window.open(webUrl, '_blank');
    }
  };

  const brandLinks = [
    { 
      name: "M-에듀캔버스", 
      url: "https://makerstory-syncedu-d75dd.web.app", 
      color: "bg-yellow-400",
      hover: "hover:bg-yellow-500"
    },
    { 
      name: "BLOG", 
      url: "https://blog.naver.com/maker_story", 
      appUrl: "naverblog://blog/maker_story",
      color: "bg-yellow-400",
      hover: "hover:bg-yellow-500"
    },
    { 
      name: "CAFE", 
      url: "https://cafe.naver.com/dgmakerstory", 
      appUrl: "navercafe://cafe/dgmakerstory",
      color: "bg-yellow-400",
      hover: "hover:bg-yellow-500"
    },
    { 
      name: "INSTAGRAM", 
      url: "https://www.instagram.com/_makerstory/", 
      appUrl: "instagram://user?username=_makerstory",
      color: "bg-yellow-400",
      hover: "hover:bg-yellow-500"
    }
  ];

  useEffect(() => {
    // 1. 초기 인트로 애니메이션 - 로고 부분 집중 (더 느리고 부드럽게 유지)
    const runIntro = async () => {
      if (!window.anime) return;

      window.anime({
        targets: '.logo-part',
        translateX: () => window.anime.random(-300, 300),
        translateY: () => window.anime.random(-300, 300),
        opacity: [0, 1],
        duration: 0
      });

      window.anime({
        targets: '.logo-part',
        translateX: 0,
        translateY: 0,
        rotate: 0,
        delay: 200,
        duration: 1500,
        easing: 'easeOutElastic(1, .5)',
      });
    };

    // 자동 공개 타이머 - 2.5초 유지
    const autoTimer = setTimeout(() => {
      handleEnter();
    }, 2500);

    runIntro();
    return () => clearTimeout(autoTimer);
  }, []);

  const handleEnter = () => {
    if (isRevealed) return;
    setIsRevealed(true);

    if (window.anime) {
      // 인트로 레이어(흰색) 사라지는 속도 - 150ms 유지
      window.anime({
        targets: blueprintRef.current,
        opacity: 0,
        duration: 150,
        easing: 'easeOutQuad',
        complete: () => {
          if (blueprintRef.current) blueprintRef.current.style.display = 'none';
        }
      });

      // 메인 버튼 등장 속도 및 딜레이 - 400ms 유지
      window.anime({
        targets: '.brush-btn-item',
        translateY: [15, 0],
        opacity: [0, 1],
        delay: window.anime.stagger(50, {start: 50}),
        duration: 400,
        easing: 'easeOutQuart'
      });
    }
  };

  // Firebase Storage 배경 이미지 경로
  const WORKSHOP_IMAGE = "https://firebasestorage.googleapis.com/v0/b/makerstory-shopping.firebasestorage.app/o/bg.jpg?alt=media&token=5849756f-15fe-4f73-9f47-60f792cc521d";

  return (
    <div className="relative w-full h-screen overflow-hidden bg-white font-sans text-white">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fugaz+One&display=swap');
        .brand-font { font-family: 'Fugaz One', cursive; }
        
        .cute-brush-filter {
          filter: url(#cute-marker-brush);
        }
        
        .brush-btn-item {
          cursor: pointer;
        }

        .marker-stroke {
          mix-blend-mode: plus-lighter;
          stroke-linecap: round;
        }

        /* 은은한 그림자 */
        .weak-text-shadow {
          text-shadow: 1px 1px 3px rgba(0, 0, 0, 0.15);
        }

        /* 서브 텍스트를 위한 강한 그림자 */
        .sub-text-shadow {
          text-shadow: 2px 2px 4px rgba(0,0,0,0.9);
        }

        @keyframes floating-subtle {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
        
        .floating-box {
          animation: floating-subtle 8s ease-in-out infinite;
        }

        .bg-layer {
          transition: opacity 1s ease-in-out, transform 2s ease-out;
        }
      `}</style>

      {/* SVG 필터 */}
      <svg className="absolute w-0 h-0" aria-hidden="true">
        <filter id="cute-marker-brush">
          <feTurbulence type="fractalNoise" baseFrequency="0.02 0.08" numOctaves="2" result="noise" />
          <feDisplacementMap in="SourceGraphic" in2="noise" scale="14" />
        </filter>
      </svg>

      {/* [레이어 0] 배경 이미지 */}
      <div 
        className={`absolute inset-0 z-0 bg-layer ${isRevealed ? 'opacity-100 scale-100' : 'opacity-0 scale-105'}`}
      >
        <img 
          src={WORKSHOP_IMAGE} 
          alt="Workshop Background" 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/35"></div>
      </div>

      {/* [레이어 1] 설계도 인트로 */}
      <div 
        ref={blueprintRef}
        onClick={handleEnter}
        className="absolute inset-0 z-[50] bg-white flex flex-col items-center justify-center cursor-pointer"
        style={{ backgroundImage: 'radial-gradient(rgba(79, 70, 229, 0.04) 1.5px, transparent 1.5px)', backgroundSize: '40px 40px' }}
      >
        <div className="relative flex flex-col items-center text-center">
          <div className="mb-12 relative w-60 h-60 flex items-center justify-center">
             <div className="logo-part absolute w-36 h-36 bg-indigo-600 rounded-[60px] rotate-45 shadow-2xl shadow-indigo-200"></div>
             <span className="logo-part absolute text-white text-[90px] italic brand-font font-black leading-none">M</span>
          </div>
          <p className="text-black text-[8px] font-black uppercase tracking-[0.8em] mt-4 animate-pulse italic ml-[0.8em]">Connecting...</p>
        </div>
      </div>

      {/* [레이어 2] 메인 컨텐츠 */}
      <div className="relative z-10 w-full h-full flex flex-col items-center justify-center px-6">
        <div className="flex flex-col md:flex-row gap-6 md:gap-16 items-center justify-center mt-32 md:mt-0">
          
          <div 
            onClick={() => navigate('/homepage')}
            className="brush-btn-item floating-box opacity-0 flex flex-col items-center group"
          >
            <div className="relative w-64 h-24 md:w-[360px] md:h-32 flex items-center justify-center">
               <svg className="absolute inset-0 w-full h-full cute-brush-filter pointer-events-none" viewBox="0 0 500 150" preserveAspectRatio="none">
                 {/* 브러시 두께 소폭 축소: 100 -> 80 */}
                 <path className="marker-stroke" d="M80,80 Q250,70 420,80" stroke="#ff4d4d" strokeWidth="80" fill="none" opacity="0.85" />
               </svg>
               {/* 텍스트 크기 한 단계 더 축소: 4xl->3xl / 7xl->6xl */}
               <span className="relative z-10 text-white text-3xl md:text-6xl font-black italic tracking-tighter brand-font select-none weak-text-shadow">
                 Homepage
               </span>
            </div>
            {/* 서브 텍스트 크기 소폭 축소: 11px -> 10px */}
            <p className="mt-0 md:-mt-2 text-white text-[10px] font-black uppercase tracking-[0.8em] italic sub-text-shadow">Brand Story</p>
          </div>

          <div 
            onClick={() => navigate('/shop')}
            className="brush-btn-item floating-box opacity-0 flex flex-col items-center group"
            style={{ animationDelay: '0.2s' }}
          >
            <div className="relative w-64 h-24 md:w-[360px] md:h-32 flex items-center justify-center">
               <svg className="absolute inset-0 w-full h-full cute-brush-filter pointer-events-none" viewBox="0 0 500 150" preserveAspectRatio="none">
                 {/* 브러시 두께 소폭 축소: 102 -> 82 */}
                 <path className="marker-stroke" d="M80,75 Q250,85 420,75" stroke="#4f46e5" strokeWidth="82" fill="none" opacity="0.85" />
               </svg>
               <span className="relative z-10 text-white text-3xl md:text-6xl font-black italic tracking-tighter brand-font select-none weak-text-shadow">
                 Shopping
               </span>
            </div>
            <p className="mt-0 md:-mt-2 text-white text-[10px] font-black uppercase tracking-[0.8em] italic sub-text-shadow">Creative Kits</p>
          </div>

        </div>
      </div>

      {/* [상단 브랜드 링크 버튼 및 배너] - DOM 순서를 마지막으로 옮겨 backdrop-blur 안정화 */}
      <div className={`absolute top-6 left-0 w-full z-[100] flex flex-col items-center gap-4 px-4 transition-all duration-1000 ${isRevealed ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}>
        <div className="flex flex-wrap justify-center items-center gap-2 md:gap-4">
          {brandLinks.map((link, idx) => (
            <button
              key={idx}
              onClick={() => handleDeepLink(link.url, link.appUrl)}
              className={`
                ${link.color} ${link.hover}
                px-3 py-1.5 md:px-5 md:py-2
                rounded-full text-[10px] md:text-xs font-black text-black hover:text-white
                shadow-lg transform transition-all duration-300
                hover:scale-110 active:scale-95
                whitespace-nowrap flex items-center justify-center
              `}
            >
              {link.name}
            </button>
          ))}
        </div>
        
        {/* 대회&자격증 뱃지/배너 */}
        <button 
          onClick={() => navigate('/competitions')}
          className="relative w-full max-w-[300px] md:max-w-[400px] mt-4 md:mt-8 overflow-hidden rounded-xl border border-yellow-400/60 shadow-2xl transform transition-all duration-300 hover:scale-108 hover:-translate-y-1 hover:border-yellow-300 active:scale-95 flex items-center justify-center group cursor-pointer"
          style={{
            animation: 'bannerPulseGlow 1.6s infinite ease-in-out'
          }}
        >
          {/* Custom self-contained styles for high-fidelity animations */}
          <style dangerouslySetInnerHTML={{__html: `
            @keyframes bannerPulseGlow {
              0%, 100% {
                box-shadow: 0 0 12px rgba(251, 191, 36, 0.45), inset 0 0 6px rgba(251, 191, 36, 0.2);
                border-color: rgba(251, 191, 36, 0.5);
              }
              50% {
                box-shadow: 0 0 25px rgba(251, 191, 36, 0.9), 0 0 8px rgba(251, 191, 36, 0.5), inset 0 0 12px rgba(251, 191, 36, 0.35);
                border-color: rgba(251, 191, 36, 1);
              }
            }
            @keyframes sweepShine {
              0% { left: -110%; }
              15%, 100% { left: 160%; }
            }
            .gprc-banner-shine {
              position: absolute;
              top: 0;
              width: 45%;
              height: 100%;
              background: linear-gradient(
                90deg, 
                transparent, 
                rgba(255, 255, 255, 0.75) 30%, 
                rgba(255, 255, 255, 0.95) 50%, 
                rgba(255, 255, 255, 0.75) 70%, 
                transparent
              );
              transform: skewX(-25deg);
              animation: sweepShine 2s infinite ease-in-out;
              pointer-events: none;
              z-index: 10;
            }
          `}} />
          
          <img 
            src="https://firebasestorage.googleapis.com/v0/b/makerstory-shopping.firebasestorage.app/o/2026_gprc_icon.png?alt=media" 
            alt="G-PRC 2026 글로벌 로봇&코딩 챔피언십" 
            className="w-full h-auto aspect-[5/1] object-cover bg-black/85 backdrop-blur-md select-none pointer-events-none"
            onError={(e) => {
              // Placeholder in case the image is not uploaded yet
              e.currentTarget.src = "https://via.placeholder.com/600x120/1a1a1a/eab308?text=G-PRC+2026";
            }}
          />

          {/* Sweeping bright light beam */}
          <div className="gprc-banner-shine" />

          {/* Hover Overlay Glow */}
          <div className="absolute inset-0 bg-yellow-400/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
        </button>
      </div>

      {/* Bottom Center Brand Text */}
      <div 
        className={`absolute bottom-6 md:bottom-8 left-1/2 -translate-x-1/2 z-30 transition-all duration-1000 ${isRevealed ? 'opacity-50 translate-y-0' : 'opacity-0 translate-y-4'} text-center w-full px-4 select-none pointer-events-none`}
      >
        <p className="text-[10px] md:text-xs font-bold tracking-[0.2em] md:tracking-[0.25em] text-white/90 uppercase drop-shadow-[0_2px_4px_rgba(0,0,0,0.6)] whitespace-nowrap">
          Makerstory: Creating the future with children
        </p>
      </div>
    </div>
  );
};

export default LandingPage;
